'use client';

import { JSX } from 'react';
import { PaymentProfileResponse } from 'subscription-service-client';

import BillingInfoCountryInput from './billingInfoCountryInput';
import BillingInfoTextInput from './billingInfoTextInput';

export default function DigitalWalletInfoForm({
    paymentProfile,
}: {
    paymentProfile: PaymentProfileResponse;
}): JSX.Element {
    const paymentMethodName = paymentProfile?.paymentMethod?.name || 'Digital Wallet';
    const billingInfo = paymentProfile?.billingInfo;

    const PAYMENT_METHOD_LOGOS: Record<string, string> = {
        'Apple Pay': '/assets/images/apple-pay-mark-rgb-041619.svg',
        'Google Pay': '/assets/images/google-pay-mark_800.svg',
        PayPal: '/assets/images/paypal-mark_80x28.svg',
    };

    // Case-insensitive lookup for payment method logo
    const getWalletLogo = () => {
        const normalizedName = paymentMethodName.toLowerCase();
        const matchingKey = Object.keys(PAYMENT_METHOD_LOGOS).find((key) => key.toLowerCase() === normalizedName);
        return matchingKey ? PAYMENT_METHOD_LOGOS[matchingKey] : null;
    };

    const walletLogo = getWalletLogo();

    return (
        <div className="flex flex-col w-full">
            <div className="flex flex-row justify-between items-center">
                <h2 className="text-2xl font-semibold my-6">Payment Information</h2>
            </div>

            {/* Digital Wallet Display */}
            <div className="py-4 border-b-1 border-gray-300 flex items-center">
                {walletLogo ? (
                    <img src={walletLogo} alt={paymentMethodName} className="h-24 w-36" />
                ) : (
                    <span className="text-lg font-semibold">{paymentMethodName}</span>
                )}
            </div>

            {/* Read-only billing information */}
            <BillingInfoTextInput label="First Name" placeholder={billingInfo?.firstName ?? ''} name="firstName" />
            <BillingInfoTextInput label="Last Name" placeholder={billingInfo?.lastName ?? ''} name="lastName" />
            {billingInfo?.postalCode && (
                <BillingInfoTextInput
                    label="Zip/Postal Code"
                    placeholder={billingInfo?.postalCode ?? ''}
                    name="postalCode"
                />
            )}
            {billingInfo?.country && <BillingInfoCountryInput initialCountry={billingInfo?.country ?? ''} />}

            {/* Information note */}
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-blue-800">
                    <strong>Note:</strong> {paymentMethodName} payment methods are managed through your{' '}
                    {paymentMethodName.toLowerCase().includes('apple') ? 'Apple' : 'Google'} account. To update your
                    payment information, please visit your{' '}
                    {paymentMethodName.toLowerCase().includes('apple') ? 'Apple Wallet' : 'Google Pay'} settings.
                </p>
            </div>
        </div>
    );
}
